import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { usdc$ } from '@/shared/assets/tokens';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import { CompactSwapRoute } from '@/shared/components/swaps/SwapRouteV2';
import { SwapStatus } from '@/shared/components/swaps/SwapStatusV2';
import { type SwapRequestOverviewFragment } from '@/shared/graphql/generated/reporting/graphql';
import TokenAmount from '@/shared/utils/TokenAmount';

const formatRatio = (inAmount: TokenAmount, outAmount: TokenAmount) =>
  outAmount.eq(0)
    ? null
    : TokenAmount.fromWholeUnits(inAmount.ratio(outAmount), usdc$.decimals).toFormatted(
        undefined,
        4,
      );

const findPrice = (swapRequest: SwapRequestOverviewFragment, asset: BaseChainflipAsset) => {
  let usdcAmount;
  let baseAssetAmount;

  if (swapRequest.sourceAsset === 'Usdc') {
    usdcAmount = swapRequest.inputAmount;
    baseAssetAmount = swapRequest.outputAmount;
  } else if (swapRequest.destAsset === 'Usdc') {
    usdcAmount = swapRequest.outputAmount;
    baseAssetAmount = swapRequest.inputAmount;
  } else if (swapRequest.sourceAsset === asset) {
    baseAssetAmount = swapRequest.inputAmount;
    const usdValueSum =
      Number(swapRequest.intermediateValueUsd || 0) + // add fees to the intermediateAmount for the first leg swap
      Number(swapRequest.networkFeeValueUsd || 0) +
      Number(swapRequest.mainBrokerFeeValueUsd || 0) +
      Number(swapRequest.affiliateBroker1FeeValueUsd || 0) +
      Number(swapRequest.affiliateBroker2FeeValueUsd || 0) +
      Number(swapRequest.affiliateBroker3FeeValueUsd || 0) +
      Number(swapRequest.affiliateBroker4FeeValueUsd || 0) +
      Number(swapRequest.affiliateBroker5FeeValueUsd || 0);

    return formatRatio(
      TokenAmount.fromWholeUnits(usdValueSum, usdc$.decimals),
      TokenAmount.fromAsset(baseAssetAmount, asset)!,
    );
  } else {
    usdcAmount = swapRequest.intermediateAmount;
    baseAssetAmount = swapRequest.outputAmount;
  }

  return formatRatio(
    // these will always be defined because a sum over empty rows will return 0
    TokenAmount.fromAsset(usdcAmount, 'Usdc')!,
    TokenAmount.fromAsset(baseAssetAmount, asset)!,
  );
};

export const PoolSwapRow = ({
  swapRequest,
  asset,
}: {
  swapRequest: SwapRequestOverviewFragment;
  asset: BaseChainflipAsset;
}) => {
  const { status } = swapRequest;
  const swapPath = `/swaps/${swapRequest.swapRequestNativeId}`;

  return (
    <ClickableTableRow className="border text-12" href={swapPath}>
      <td className="whitespace-nowrap">
        <div className="flex flex-row items-center space-x-2">
          <Link href={swapPath} underline noPropagate>
            #{swapRequest.swapRequestNativeId}
          </Link>
        </div>
      </td>
      <td>
        <div className="inline-flex">
          <CompactSwapRoute
            routeInfo={{
              destinationAsset: swapRequest.destAsset,
              sourceAsset: swapRequest.sourceAsset,
            }}
            swapRequest={swapRequest}
            highlightAssets={[asset, 'Usdc']}
            status={status}
            showIntermediate
            hideUsdValue
          />
        </div>
      </td>

      <td>
        <SwapStatus status={status} />
      </td>

      <td>
        <span>{findPrice(swapRequest, asset)}</span>
      </td>

      <td className="whitespace-nowrap text-right text-cf-light-2">
        {swapRequest.startedBlockTimestamp && (
          <AgeWithTooltip timestamp={swapRequest.startedBlockTimestamp} />
        )}
      </td>
    </ClickableTableRow>
  );
};
